import { IS_APPLE } from '@lexical/utils'

//disable eslint sorting rule for quick reference to shortcuts
export const SHORTCUTS = Object.freeze({
  // (Ctrl|⌘)   (Alt|Option)   <key> shortcuts
  NORMAL: IS_APPLE ? '⌘ Opt 0' : 'Ctrl Alt 0',
  HEADING1: IS_APPLE ? '⌘ Opt 1' : 'Ctrl Alt 1',
  HEADING2: IS_APPLE ? '⌘ Opt 2' : 'Ctrl Alt 2',
  HEADING3: IS_APPLE ? '⌘ Opt 3' : 'Ctrl Alt 3',
  HEADING4: IS_APPLE ? '⌘ Opt 4' : 'Ctrl Alt 4',
  BULLET_LIST: IS_APPLE ? '⌘ Opt 4' : 'Ctrl Alt 5',
  NUMBERED_LIST: IS_APPLE ? '⌘ Opt 5' : 'Ctrl Alt 6',
  CHECK_LIST: IS_APPLE ? '⌘ Opt 6' : 'Ctrl Alt 7',
  CODE_BLOCK: IS_APPLE ? '⌘ Opt C' : 'Ctrl Alt C',
  QUOTE: IS_APPLE ? '⌘ Opt Q' : 'Ctrl Alt Q',
  HORIZONTAL_RULE: IS_APPLE ? '⌘ Opt L' : 'Ctrl Alt L',
  FONT_COLOR: IS_APPLE ? '⌘ Opt C' : 'Ctrl Alt C',
  FONT_BACKGROUND_COLOR: IS_APPLE ? '⌘ Opt B' : 'Ctrl Alt B',
  FONT_SIZE: IS_APPLE ? '⌘ Shift + / -' : 'Ctrl Alt + / -',

  // (Ctrl|⌘)   Shift   <key> shortcuts
  INSERT_CODE_BLOCK: IS_APPLE ? '⌘ Shift C' : 'Ctrl Shift C',
  STRIKETHROUGH: IS_APPLE ? '⌘ Shift X' : 'Ctrl Shift X',
  TEXT_ALIGN_CENTER: IS_APPLE ? '⌘ Shift E' : 'Ctrl Shift E',
  TEXT_ALIGN_JUSTIFY: IS_APPLE ? '⌘ Shift J' : 'Ctrl Shift J',
  TEXT_ALIGN_LEFT: IS_APPLE ? '⌘ Shift L' : 'Ctrl Shift L',
  TEXT_ALIGN_RIGHT: IS_APPLE ? '⌘ Shift R' : 'Ctrl Shift R',
  SUPERSCRIPT: IS_APPLE ? '⌘ Shift .' : 'Ctrl Shift .',
  SUBSCRIPT: IS_APPLE ? '⌘ Shift ,' : 'Ctrl Shift ,',
  FORMAT_PAINTER: IS_APPLE ? '⌘ Shift F' : 'Ctrl Shift F',

  // (Ctrl|⌘)   <key> shortcuts
  INDENT: IS_APPLE ? '⌘ ]' : 'Ctrl ]',
  OUTDENT: IS_APPLE ? '⌘ [' : 'Ctrl [',
  CLEAR_FORMATTING: IS_APPLE ? '⌘ \\' : 'Ctrl \\',
  REDO: IS_APPLE ? '⌘ Shift Z' : 'Ctrl Y',
  UNDO: IS_APPLE ? '⌘ Z' : 'Ctrl Z',
  BOLD: IS_APPLE ? '⌘ B' : 'Ctrl B',
  ITALIC: IS_APPLE ? '⌘ I' : 'Ctrl I',
  UNDERLINE: IS_APPLE ? '⌘ U' : 'Ctrl U',
  INSERT_LINK: IS_APPLE ? '⌘ K' : 'Ctrl K'
})

function controlOrMeta(metaKey: boolean, ctrlKey: boolean): boolean {
  return IS_APPLE ? metaKey : ctrlKey
}

function ctrlShiftCombo(
  event: KeyboardEvent,
  eq: (code: string) => boolean
): boolean {
  const { code, shiftKey, altKey, metaKey, ctrlKey } = event
  return eq(code) && controlOrMeta(metaKey, ctrlKey) && shiftKey && !altKey
}

function ctrlAltCombo(
  event: KeyboardEvent,
  eq: (code: string) => boolean
): boolean {
  const { code, shiftKey, altKey, metaKey, ctrlKey } = event
  return eq(code) && controlOrMeta(metaKey, ctrlKey) && !shiftKey && altKey
}

function ctrlCombo(
  event: KeyboardEvent,
  eq: (code: string) => boolean
): boolean {
  const { code, shiftKey, altKey, metaKey, ctrlKey } = event
  return eq(code) && controlOrMeta(metaKey, ctrlKey) && !shiftKey && !altKey
}

export function isFormatParagraph(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'Numpad0' || code === 'Digit0')
}

export function isFormatHeading(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => {
    const keyNumber = code[code.length - 1]
    return ['1', '2', '3', '4'].includes(keyNumber)
  })
}

export function isFormatBulletList(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'Numpad5' || code === 'Digit5')
}

export function isFormatNumberedList(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'Numpad6' || code === 'Digit6')
}

export function isFormatCheckList(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'Numpad7' || code === 'Digit7')
}

export function isFormatCode(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'KeyC')
}

export function isFormatQuote(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'KeyQ')
}

export function isHorizontalRule(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'KeyL')
}

export function isFontColor(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'KeyC')
}

export function isFontBackgroundColor(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'KeyB')
}

export function isIncreaseFontSize(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'Equal')
}

export function isDecreaseFontSize(event: KeyboardEvent): boolean {
  return ctrlAltCombo(event, code => code === 'Minus')
}

export function isStrikeThrough(event: KeyboardEvent): boolean {
  return ctrlShiftCombo(event, code => code === 'KeyX')
}

export function isCenterAlign(event: KeyboardEvent): boolean {
  return ctrlShiftCombo(event, code => code === 'KeyE')
}

export function isLeftAlign(event: KeyboardEvent): boolean {
  return ctrlShiftCombo(event, code => code === 'KeyL')
}

export function isRightAlign(event: KeyboardEvent): boolean {
  return ctrlShiftCombo(event, code => code === 'KeyR')
}

export function isJustifyAlign(event: KeyboardEvent): boolean {
  return ctrlShiftCombo(event, code => code === 'KeyJ')
}

export function isSubscript(event: KeyboardEvent): boolean {
  return ctrlShiftCombo(event, code => code === 'Comma')
}

export function isSuperscript(event: KeyboardEvent): boolean {
  return ctrlShiftCombo(event, code => code === 'Period')
}

export function isFormatPainter(event: KeyboardEvent): boolean {
  return ctrlShiftCombo(event, code => code === 'KeyF')
}

export function isInsertCodeBlock(event: KeyboardEvent): boolean {
  return ctrlShiftCombo(event, code => code === 'KeyC')
}

export function isClearFormatting(event: KeyboardEvent): boolean {
  return ctrlCombo(event, code => code === 'Backslash')
}

export function isInsertLink(event: KeyboardEvent): boolean {
  return ctrlCombo(event, code => code === 'KeyK')
}

export function isIndent(event: KeyboardEvent): boolean {
  return ctrlCombo(event, code => code === 'BracketRight')
}

export function isOutdent(event: KeyboardEvent): boolean {
  return ctrlCombo(event, code => code === 'BracketLeft')
}
